import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import LogoLoader from '../../components/LogoLoader';
import './BookStore.css';

const OwnBookReader = () => {
    const { bookId } = useParams();
    const [book, setBook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fontSize, setFontSize] = useState(2); // 0-4 indices for sizes
    const [theme, setTheme] = useState('standard'); // 'standard' or 'warm'
    const [showSettings, setShowSettings] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [touchStart, setTouchStart] = useState(null);

    const settingsRef = useRef(null);
    const containerRef = useRef(null);
    const contentRef = useRef(null);

    const fontSizes = [
        'clamp(0.85rem, 2.2vw, 1.05rem)',
        'clamp(0.95rem, 2.5vw, 1.2rem)',
        'clamp(1.1rem, 3vw, 1.4rem)',
        'clamp(1.25rem, 3.5vw, 1.65rem)',
        'clamp(1.4rem, 4.2vw, 2rem)'
    ];
    
    useEffect(() => {
        const fetchBook = async () => {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            try {
                const response = await axios.get(API_ENDPOINTS.OWN_BOOKS.GET_SINGLE(bookId), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data.success) {
                    setBook(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch book:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchBook();
    }, [bookId]);

    // Calculate total pages
    useEffect(() => {
        const calculatePages = () => {
            if (contentRef.current && containerRef.current) {
                // The browser splits text into columns horizontally. 
                // Total scrollWidth / container width = total pages.
                const scrollWidth = contentRef.current.scrollWidth;
                const clientWidth = containerRef.current.clientWidth;
                const pages = Math.ceil(scrollWidth / clientWidth);
                setTotalPages(pages || 1);
                
                setCurrentPage(prev => {
                    if (prev >= pages) return Math.max(0, pages - 1);
                    return prev;
                });
            }
        };

        // Delay slightly to let fonts/DOM render
        setTimeout(calculatePages, 100);
        
        window.addEventListener('resize', calculatePages);
        return () => window.removeEventListener('resize', calculatePages);
    }, [book, fontSize]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (settingsRef.current && !settingsRef.current.contains(event.target)) {
                setShowSettings(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Keyboard navigation handlers
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight') {
                setCurrentPage(prev => (prev < totalPages - 1 ? prev + 1 : prev));
            } else if (e.key === 'ArrowLeft') {
                setCurrentPage(prev => (prev > 0 ? prev - 1 : prev));
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [totalPages]);

    // Swipe gesture handlers
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = (e) => {
        if (!touchStart) return;
        const touchEnd = e.changedTouches[0].clientX;
        const distance = touchStart - touchEnd;
        
        if (distance > 50 && currentPage < totalPages - 1) {
            setCurrentPage(prev => prev + 1);
        } else if (distance < -50 && currentPage > 0) {
            setCurrentPage(prev => prev - 1);
        }
        setTouchStart(null);
    };

    // Tap zones for desktop/quick navigation
    const handleContentClick = (e) => {
        const x = e.clientX;
        const width = window.innerWidth;
        if (x > width * 0.7 && currentPage < totalPages - 1) {
            setCurrentPage(p => p + 1);
        } else if (x < width * 0.3 && currentPage > 0) {
            setCurrentPage(p => p - 1);
        }
    };

    if (loading) return <LogoLoader progress={50} />;
    if (!book) return <div className="text-white p-10 text-center">Book not found.</div>;

    const readerStyle = {
        backgroundColor: theme === 'warm' ? '#FAEFD9' : 'var(--bg-color)',
        color: theme === 'warm' ? '#333' : 'var(--text-color)',
        transition: 'background-color 0.3s ease, color 0.3s ease',
        minHeight: '100vh',
        overflow: 'hidden',
        position: 'fixed' as const,
        inset: 0,
        fontFamily: '"Georgia", serif'
    };

    return (
        <div style={readerStyle} className="own-book-reader-container">
            <Helmet>
                <title>{book.productName} | Reader | Hari Vishnu</title>
                <link href="https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&display=swap" rel="stylesheet" />
            </Helmet>

            <div className="reader-header" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                padding: '20px 5%',
                background: theme === 'warm' ? '#FAEFD9' : 'var(--bg-color)',
                backdropFilter: 'blur(10px)',
                zIndex: 100,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme === 'warm' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
                fontFamily: 'sans-serif', // Hardcoded to prevent text size setting interference
                fontSize: '1rem',
            }}>
                <Link to="/app-store/bookstore/own-books" style={{ color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
                    <i className="fi fi-rr-arrow-left"></i> Library
                </Link>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', opacity: 0.8 }}>{book.productName}</div>
                <div style={{ width: '40px' }}></div> {/* Spacer */}
            </div>

            <div style={{
                position: 'fixed',
                top: '60px',
                bottom: '0',
                left: '0',
                right: '0',
                display: 'flex',
                justifyContent: 'center',
                overflow: 'hidden'
            }}>
                <div 
                    ref={containerRef}
                    className="reader-viewport" 
                    style={{
                        width: '100%',
                        height: '100%',
                        minHeight: 0,
                        padding: '0 5vw',
                        position: 'relative',
                        cursor: 'pointer',
                        userSelect: 'none',
                        overflow: 'hidden'
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onClick={handleContentClick}
                >
                    <div 
                        ref={contentRef}
                        className="reader-content whitespace-pre-wrap"
                        style={{
                            columnWidth: '100vw',
                            columnGap: '10vw', // Match total horizontal padding (5vw left + 5vw right)
                            columnFill: 'auto',
                            height: '100%',
                            padding: '40px 0 120px 0',
                            boxSizing: 'border-box',
                            transform: `translateX(calc(-${currentPage} * 100vw))`,
                            transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                            textAlign: 'justify',
                            fontSize: fontSizes[fontSize],
                            lineHeight: '1.8'
                        }}
                    >
                        {book.extractedText?.text?.trim() ? book.extractedText.text.trim() : (
                            <div className="flex flex-col items-center justify-center py-20 opacity-50 text-center" style={{ fontFamily: 'sans-serif' }}>
                                <i className="fi fi-rr-info text-5xl mb-6"></i>
                                <p className="text-xl font-medium">No text could be extracted from this PDF.</p>
                                <p className="text-sm mt-3 max-w-[400px]">
                                    This usually happens if the PDF contains scanned images or if the file was lost due to server restarts.
                                </p>
                                <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10">
                                    <p className="text-sm font-bold text-orange-400 mb-2">PRO TIP</p>
                                    <p className="text-xs">Try deleting and re-uploading this book. Our new extraction engine will attempt to process it immediately upon upload.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Page Indicator */}
            <div style={{
                position: 'fixed',
                bottom: '30px',
                left: '0',
                right: '0',
                textAlign: 'center',
                pointerEvents: 'none',
                opacity: 0.5,
                fontSize: '0.8rem',
                fontFamily: 'sans-serif',
                zIndex: 90
            }}>
                Page {currentPage + 1} of {totalPages}
            </div>

            <div className="reader-footer-actions" style={{ position: 'fixed', bottom: '30px', right: '30px', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                {showSettings && (
                    <div 
                        ref={settingsRef}
                        className="reader-settings-popup"
                        style={{
                            marginBottom: '10px',
                            width: '180px',
                            background: '#ffffff',
                            borderRadius: '12px',
                            padding: '16px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(0,0,0,0.05)',
                            position: 'relative',
                            fontFamily: '"Georgia", serif'
                        }}
                    >
                        {/* Caret pointing down to the button */}
                        <div style={{
                            position: 'absolute',
                            bottom: '-6px',
                            right: '18px',
                            width: '12px',
                            height: '12px',
                            background: '#ffffff',
                            transform: 'rotate(45deg)',
                            borderRight: '1px solid rgba(0,0,0,0.05)',
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                        }}></div>

                        <div className="settings-section mb-4 text-center">
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '12px', color: '#555', fontWeight: 500 }}>Text Size</label>
                            <div className="flex items-center justify-between px-1">
                                {[0, 1, 2, 3, 4].map((index) => (
                                    <button 
                                        key={index}
                                        onClick={(e) => { e.stopPropagation(); setFontSize(index); }}
                                        style={{ 
                                            fontSize: `${0.7 + index * 0.15}rem`, 
                                            color: fontSize === index ? '#4a90e2' : '#555', 
                                            background: fontSize === index ? '#e6f0fa' : 'transparent', 
                                            border: 'none', 
                                            cursor: 'pointer',
                                            padding: '4px',
                                            borderRadius: '6px',
                                            minWidth: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: fontSize === index ? 600 : 400
                                        }}
                                    >A</button>
                                ))}
                            </div>
                        </div>

                        <div style={{ height: '1px', background: '#eaeaea', margin: '0 8px 12px 8px' }}></div>

                        <div className="settings-section text-center">
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '12px', color: '#555', fontWeight: 500 }}>Reading Theme</label>
                            <div className="flex justify-center gap-4">
                                {/* Light Theme Button */}
                                <button 
                                    onClick={(e) => { e.stopPropagation(); setTheme('standard'); }}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #ffffff 50%, #000000 50%)',
                                        border: 'none',
                                        boxShadow: theme === 'standard' 
                                            ? '0 0 0 2px #fff, 0 0 0 4px #4a90e2, 0 2px 5px rgba(0,0,0,0.2)' 
                                            : '0 2px 5px rgba(0,0,0,0.2)',
                                        cursor: 'pointer',
                                        padding: 0
                                    }}
                                    aria-label="Light Theme"
                                />
                                
                                {/* Warm Theme Button */}
                                <button 
                                    onClick={(e) => { e.stopPropagation(); setTheme('warm'); }}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #f4e8d3 50%, #000000 50%)',
                                        border: 'none',
                                        boxShadow: theme === 'warm' 
                                            ? '0 0 0 2px #fff, 0 0 0 4px #4a90e2, 0 2px 5px rgba(0,0,0,0.2)' 
                                            : '0 2px 5px rgba(0,0,0,0.2)',
                                        cursor: 'pointer',
                                        padding: 0
                                    }}
                                    aria-label="Warm Theme"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <button 
                    className="reader-settings-btn"
                    onClick={(e) => { e.stopPropagation(); setShowSettings(!showSettings); }}
                    style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        color: '#4a90e2',
                        border: '1px solid rgba(0,0,0,0.1)',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        transition: 'transform 0.2s',
                        transform: showSettings ? 'scale(1.05)' : 'scale(1)'
                    }}
                >
                    {/* Format icon instead of sliders to match image hint */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="4 7 4 4 20 4 20 7"></polyline>
                        <line x1="9" y1="20" x2="15" y2="20"></line>
                        <line x1="12" y1="4" x2="12" y2="20"></line>
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default OwnBookReader;
